import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Upload, Trash2, Stamp, PenLine, Building2, Image } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TraegerImageAdjust } from "@/components/TraegerImageAdjust";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
const FIELDS = [
  ["traeger_name", "Name des Trägers"], ["traeger_anschrift", "Anschrift"], ["durchfuehrungsort", "Durchführungsort"],
  ["traeger_telefon", "Telefon"], ["traeger_email", "E-Mail"], ["ansprechpartner", "Ansprechpartner"], ["ort", "Ort (für „Ort, Datum“)"],
];
const BRAND_FIELDS = [
  ["portal_name", "Portal-Name (in E-Mails)"], ["geschaeftsfuehrung", "Geschäftsführung"], ["handelsregister", "Handelsregister-Nr."],
  ["website", "Website"], ["impressum_url", "Impressum-Link"], ["datenschutz_url", "Datenschutz-Link"],
];
const IMAGES = [
  ["logo", "Logo", Image, "Firmenlogo für alle E-Mails, PDFs und das Portal (PNG/JPG, quer)."],
  ["stamp", "Stempel", Stamp, "Firmenstempel als PNG (transparent/weiß) oder JPG."],
  ["signature", "Unterschrift", PenLine, "Unterschrift auf weißem Papier, eingescannt oder fotografiert (PNG/JPG)."],
];

function ImageEditor({ kind, label, Icon, hint, has, onChange }) {
  const ref = useRef(null);
  const [url, setUrl] = useState(null);
  const [ver, setVer] = useState(0);
  useEffect(() => {
    let u;
    if (has) api.get(`/traeger-forms/profile/image/${kind}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    else setUrl(null);
    return () => u && URL.revokeObjectURL(u);
  }, [has, ver, kind]);
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try { const r = await api.post(`/traeger-forms/profile/image/${kind}`, fd); onChange(r.data); setVer((v) => v + 1); toast.success(`${label} gespeichert`); }
    catch (e) { err(e); }
  };
  const remove = () => api.delete(`/traeger-forms/profile/image/${kind}`).then((r) => { onChange(r.data); toast.success(`${label} entfernt`); }).catch(err);
  return (
    <div className="border border-slate-200 p-3 flex flex-wrap items-center gap-4" data-testid={`traeger-image-${kind}`}>
      <div className="w-40 h-20 border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden">
        {url ? <img src={url} alt={label} className="max-h-full max-w-full object-contain" data-testid={`traeger-image-preview-${kind}`} /> : <Icon size={22} className="text-slate-300" />}
      </div>
      <div className="text-xs text-slate-500 flex-1 min-w-[160px]"><div className="font-medium text-slate-800 text-sm">{label}</div>{hint}</div>
      <input ref={ref} type="file" accept="image/png,image/jpeg" className="hidden" data-testid={`traeger-image-input-${kind}`}
        onChange={(e) => { if (e.target.files[0]) upload(e.target.files[0]); e.target.value = ""; }} />
      <button onClick={() => ref.current?.click()} className="inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50" data-testid={`traeger-image-upload-${kind}`}><Upload size={13} /> {has ? "Ersetzen" : "Hochladen"}</button>
      {has && <button onClick={remove} className="p-2 border border-slate-200 text-rose-600 hover:bg-rose-50" data-testid={`traeger-image-delete-${kind}`}><Trash2 size={13} /></button>}
    </div>
  );
}

export function MissingProfileHint({ missing }) {
  if (!missing?.length) return null;
  return (
    <div className="border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="traeger-profile-missing">
      Für vollständige Trägerbestätigungen fehlen noch: <b>{missing.join(", ")}</b>. Bitte unter „Trägerdaten, Stempel & Unterschrift“ ergänzen (hier aufklappen oder Management → Trägerdaten, Stempel & Unterschrift).
    </div>
  );
}

export default function TraegerProfileEditor({ onSaved, collapsible = false, onProfile, show = "all", onlyImages = null }) {
  const [p, setP] = useState(null);
  const [open, setOpen] = useState(!collapsible);
  const [imgVer, setImgVer] = useState(0);
  const update = (d) => { setP(d); onProfile?.(d); };
  useEffect(() => { api.get("/traeger-forms/profile").then((r) => update(r.data)).catch(err); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const fields = show === "images" ? [] : show === "all" ? [...FIELDS, ...BRAND_FIELDS] : FIELDS.concat(BRAND_FIELDS);
  const save = () => api.put("/traeger-forms/profile", Object.fromEntries(fields.map(([k]) => [k, p[k] || ""])))
    .then((r) => { update(r.data); toast.success("Trägerdaten gespeichert"); onSaved?.(); }).catch(err);
  if (!p) return null;
  return (
    <div className="border border-slate-200 bg-white" data-testid="traeger-profile">
      {collapsible && (
        <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-4 py-3 text-sm text-slate-700 hover:bg-slate-50" data-testid="traeger-profile-toggle">
          <Building2 size={14} /> Trägerdaten, Stempel & Unterschrift (für alle Vordrucke) {open ? "▴" : "▾"}
        </button>
      )}
      {open && (
        <div className="p-4 space-y-4">
          {!collapsible && <MissingProfileHint missing={(p.missing || []).filter((m) => (show === "images") === ["Stempel", "Unterschrift"].includes(m) || show === "all")} />}
          {fields.length > 0 && <div className="grid sm:grid-cols-2 gap-3">
            {fields.map(([k, l]) => (
              <label key={k} className="text-xs text-slate-500">{l}
                <input value={p[k] || ""} onChange={(e) => setP({ ...p, [k]: e.target.value })} data-testid={`traeger-profile-${k}`}
                  className="mt-1 w-full border border-slate-200 px-2 py-1.5 text-sm text-slate-900" />
              </label>
            ))}
          </div>}
          {fields.length > 0 && <button onClick={save} className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2 text-sm hover:bg-slate-700" data-testid="traeger-profile-save">
            <Save size={14} /> Trägerdaten speichern
          </button>}
          {show !== "fields" && <div className="grid lg:grid-cols-2 gap-3">
            {IMAGES.filter(([k]) => !onlyImages || onlyImages.includes(k)).map(([k, l, Icon, hint]) => <ImageEditor key={k} kind={k} label={l} Icon={Icon} hint={hint} has={p[`has_${k}`]} onChange={(d) => { update(d); setImgVer((v) => v + 1); }} />)}
          </div>}
          {show !== "fields" && (!onlyImages || onlyImages.includes("stamp")) && (
            <TraegerImageAdjust key={imgVer} initial={p.image_adjust} hasImages={p.has_stamp || p.has_signature} />
          )}
          <p className="text-xs text-slate-500">Stempel und Unterschrift werden automatisch über „Unterschrift“ bzw. „Firmenstempel“ jeder Trägerbestätigung gesetzt. Größe und Position oben anpassen.</p>
        </div>
      )}
    </div>
  );
}
